import React, { forwardRef } from 'react';
import cx from 'clsx';

import { IconBtnLoading } from '@/assets';

import style from './index.module.css';

export interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  loading?: boolean;
  showLoadingChildren?: boolean;
  block?: boolean;
  children?: React.ReactNode;
  ghost?: boolean;
  primary?: boolean;
  shadow?: boolean;
  type?: 'primary' | 'danger' | 'default' | 'text' | 'link' | 'green' | 'dialog' | 'gray';
  htmlType?: 'submit';
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const {
    className,
    disabled,
    onClick,
    loading,
    children,
    type = 'default',
    ghost,
    block,
    shadow,
    htmlType,
    showLoadingChildren,
    ...rest
  } = props;

  // chrome: mouseleave don't trigger on disabled inputs and button
  // https://github.com/react-component/tooltip/issues/18
  // so use fake disable instead
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled || loading) {
      return;
    }

    onClick?.(e);
  };

  return (
    <button
      {...rest}
      className={cx(
        style.button,
        style[`is_${type}`],
        ghost && style.is_ghost,
        block && style.is_block,
        shadow && style.is_shadow,
        (loading || disabled) && style.is_disabled,
        className,
      )}
      type={htmlType}
      aria-disabled={disabled}
      onClick={handleClick}
      ref={ref}>
      {loading ? (
        <>
          <IconBtnLoading
            className={cx(style.loadingIcon, showLoadingChildren && style.loadingIconWithChildren)}
            width={24}
            height={24}
          />
          {showLoadingChildren ? children : null}
        </>
      ) : (
        children
      )}
    </button>
  );
});

export default Button;
